import { useEffect, useState } from "react";
import { uuidv7 } from "../../../core/platform/id";
import { formatQuantity, parsePesos, parseQuantity, QuantityError } from "../quantity";
import type { Ingredient, InventoryApi } from "./api";
import {
  booksApi,
  PAYABLE_FROM,
  type PlaceId,
  type SpendingCategory,
} from "./spending";

/**
 * Count, delivery, or wastage — one form, three modes (T029, T032, FR-073).
 *
 * THE opId IS MINTED WHEN THE FORM OPENS, not when it is submitted, so a
 * double tap or a retry after a dropped connection sends the same id twice and
 * the server records it once.
 *
 * Quantities are typed in the ingredient's own unit ("0.25" litres); the
 * delivery price in pesos per unit. Neither passes through a float.
 *
 * A DELIVERY CAN ALSO BE THE PURCHASE (T015, FR-129). When the books are
 * switched on and this person may write to them, the delivery offers to record
 * what was paid, and the two records are linked by this form's opId — one
 * entry, never two.
 */

export type MovementKind = "count" | "receiving" | "wastage";

const COPY: Record<MovementKind, { title: string; quantity: string; submit: string }> = {
  count: { title: "Count", quantity: "On the shelf now", submit: "Record count" },
  receiving: { title: "Delivery", quantity: "Delivered", submit: "Record delivery" },
  wastage: { title: "Used or wasted", quantity: "Used", submit: "Record usage" },
};

/**
 * One tap for the usual reasons stock leaves without a sale — a shoot, a staff
 * drink. Tapping fills the reason; it stays editable, so "Photo shoot — menu
 * board" is still possible, and the verbatim text is what is kept (FR-121).
 */
const USAGE_REASONS = ["Photo/video shoot", "Staff drink", "Sample/tasting", "Damaged/defective", "Spilled"];

const today = () => new Date().toISOString().slice(0, 10);

export interface MovementFormProps {
  api: InventoryApi;
  token: string;
  ingredient: Ingredient;
  kind: MovementKind;
  onDone(message: string): void;
  onCancel(): void;
}

export function MovementForm({ api, token, ingredient, kind, onDone, onCancel }: MovementFormProps) {
  const [opId] = useState(() => uuidv7());
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState(kind === "count" && ingredient.countedAt === null ? "Opening count" : "");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Null until we know the books are switched on and open to this person. */
  const [categories, setCategories] = useState<SpendingCategory[] | null>(null);
  const [alsoSpending, setAlsoSpending] = useState(false);
  const [supplier, setSupplier] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [place, setPlace] = useState<PlaceId>("ewallet");
  const [paid, setPaid] = useState("");

  const copy = COPY[kind];
  const unitWord = ingredient.unit === "piece" ? "pieces" : ingredient.unit;

  useEffect(() => {
    if (kind !== "receiving") return;

    let live = true;
    void booksApi(token)
      .categories()
      .then((body) => {
        if (!live) return;
        // Goods first — a delivery is nearly always one of them.
        const sorted = [...body.categories].sort(
          (a, b) => Number(b.kind === "goods") - Number(a.kind === "goods"),
        );
        setCategories(sorted);
        setCategoryId((current) => current || sorted[0]?.id || "");
      })
      .catch(() => {
        // Switched off, not permitted, or offline — just record the delivery.
        if (live) setCategories(null);
      });

    return () => {
      live = false;
    };
  }, [kind, token]);

  const submit = async () => {
    setError(null);

    let amount: number;
    let unitCost: number | null = null;
    try {
      amount = parseQuantity(quantity);
      if (kind === "receiving" && price.trim() !== "") unitCost = parsePesos(price);
    } catch (caught) {
      setError(caught instanceof QuantityError || caught instanceof Error ? caught.message : "Check the numbers.");
      return;
    }
    if (kind !== "count" && amount === 0) {
      setError("Enter how much.");
      return;
    }
    if (kind !== "receiving" && reason.trim() === "") {
      // FR-121 — the server and the table refuse it too; saying so here is kinder.
      setError(kind === "wastage" ? "Say what it was used for." : "Say why it was counted.");
      return;
    }

    /*
     * What was paid. Derived from the price per unit only when that division is
     * exact — money is never silently rounded, so an awkward price is typed in
     * full rather than guessed at.
     */
    let spent = 0;
    const recordingSpending = kind === "receiving" && categories !== null && alsoSpending;
    if (recordingSpending) {
      if (paid.trim() === "") {
        const product = unitCost === null ? null : unitCost * amount;
        if (product === null || product % 1000 !== 0) {
          setError("Enter what was paid for this delivery.");
          return;
        }
        spent = product / 1000;
      } else {
        try {
          spent = parsePesos(paid);
        } catch (caught) {
          setError(caught instanceof Error ? caught.message : "That is not an amount.");
          return;
        }
      }
      if (spent === 0) {
        setError("Enter what was paid for this delivery.");
        return;
      }
      if (categoryId === "") {
        setError("Choose what kind of spending this was.");
        return;
      }
    }

    setBusy(true);
    try {
      if (kind === "count") {
        const { variance } = await api.count({
          opId,
          ingredientId: ingredient.id,
          countedQuantity: amount,
          reason: reason.trim(),
        });
        onDone(
          variance === 0
            ? `${ingredient.name} counted — it matched the books.`
            : `${ingredient.name} counted. The books were ${variance > 0 ? "short" : "over"} by ${formatQuantity(Math.abs(variance), ingredient.unit)}, now corrected.`,
        );
      } else if (kind === "receiving") {
        await api.receiving({ opId, ingredientId: ingredient.id, quantity: amount, unitCost });

        /*
         * Stock first, then the money, both keyed on this form's opId. A
         * failure between them is repaired by pressing Save again: the
         * movement already written is recognised as a duplicate, and the
         * expense lands exactly once.
         */
        if (recordingSpending) {
          await booksApi(token).createExpense({
            opId,
            spentOn: today(),
            supplier: supplier.trim() || null,
            description: `${formatQuantity(amount, ingredient.unit)} of ${ingredient.name}`,
            categoryId,
            amount: spent,
            parts: [{ place, amount: spent }],
            inventoryOpId: opId,
          });
        }

        onDone(
          `${formatQuantity(amount, ingredient.unit)} of ${ingredient.name} received.` +
            (recordingSpending ? " Recorded as spending too." : ""),
        );
      } else {
        await api.wastage({ opId, ingredientId: ingredient.id, quantity: amount, reason: reason.trim() });
        onDone(`${formatQuantity(amount, ingredient.unit)} of ${ingredient.name} written off.`);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not record it");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="inv__panel inv__form" aria-label={`${copy.title}: ${ingredient.name}`}>
      <h3 className="inv__detail-name">
        {copy.title} — {ingredient.name}
      </h3>
      {kind === "count" && (
        <p className="inv__hint">
          The books say {formatQuantity(ingredient.currentStock, ingredient.unit)}. Enter what is really
          there; the difference is recorded, not hidden.
        </p>
      )}

      <label className="inv__field">
        <span>
          {copy.quantity} ({unitWord})
        </span>
        <input
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
          inputMode="decimal"
          placeholder="e.g. 1.5"
          autoFocus
        />
      </label>

      {kind === "receiving" && (
        <label className="inv__field">
          <span>Price per {ingredient.unit === "piece" ? "piece" : ingredient.unit}, in pesos — optional</span>
          <input
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            inputMode="decimal"
            placeholder="e.g. 85.00"
          />
        </label>
      )}

      {kind === "receiving" && categories !== null && (
        <fieldset className="inv__field">
          <label className="inv__check">
            <input
              type="checkbox"
              checked={alsoSpending}
              onChange={(event) => setAlsoSpending(event.target.checked)}
            />
            <span>Also record what this cost — one entry, no second trip to the books</span>
          </label>

          {alsoSpending && (
            <>
              <label className="inv__field">
                <span>Supplier</span>
                <input value={supplier} onChange={(event) => setSupplier(event.target.value)} maxLength={200} />
              </label>

              <label className="inv__field">
                <span>Kind of spending</span>
                <select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="inv__field">
                <span>Paid from</span>
                <select value={place} onChange={(event) => setPlace(event.target.value as PlaceId)}>
                  {PAYABLE_FROM.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="inv__field">
                <span>Paid in total, in pesos — from the price above if left blank</span>
                <input
                  value={paid}
                  onChange={(event) => setPaid(event.target.value)}
                  inputMode="decimal"
                  placeholder="e.g. 1,020.00"
                />
              </label>
            </>
          )}
        </fieldset>
      )}

      {kind === "wastage" && (
        <div className="inv__reasons" role="group" aria-label="Common reasons">
          {USAGE_REASONS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={`inv__reason${reason === preset ? " is-selected" : ""}`}
              aria-pressed={reason === preset}
              onClick={() => setReason(preset)}
            >
              {preset}
            </button>
          ))}
        </div>
      )}

      {kind !== "receiving" && (
        <label className="inv__field">
          <span>{kind === "wastage" ? "What it was for" : "Why"}</span>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={500}
            placeholder={kind === "wastage" ? "Tap one above, or e.g. Jug knocked over" : "e.g. Month-end count"}
          />
        </label>
      )}

      {error && (
        <p className="inv__error" role="alert">
          {error}
        </p>
      )}

      <div className="inv__buttons">
        <button type="button" className="inv__action" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="inv__action is-primary" onClick={() => void submit()} disabled={busy}>
          {busy ? "Saving…" : copy.submit}
        </button>
      </div>
    </section>
  );
}
